
import { useSpotifyUrlValidation } from './validateSpotifyUrl';
import { useTrackPreview } from './useTrackPreview';
import { useFileUpload } from './useFileUpload';
import { useUploadProcess } from './useUploadProcess';
import { useTrackDetails } from './useTrackDetails';
import { TrackDownload } from '@/lib/types';

export function useTrackUpload(artistId: string | null, onUploadSuccess: (upload: TrackDownload) => void) {
  const { 
    spotifyUrl, 
    isUrlValid, 
    validateSpotifyUrl, 
    handleSpotifyUrlChange 
  } = useSpotifyUrlValidation();
  
  const { 
    isPreviewLoading, 
    trackPreview, 
    setTrackPreview, 
    handlePreviewTrack 
  } = useTrackPreview();
  
  const { 
    selectedFile, 
    handleFileChange 
  } = useFileUpload();
  
  const {
    trackTitle,
    trackDescription,
    setTrackTitle,
    setTrackDescription
  } = useTrackDetails();
  
  const { 
    isUploading, 
    uploadError,
    handleUpload 
  } = useUploadProcess(artistId, onUploadSuccess);

  // Although we've removed the preview button, we'll still load the preview
  // when validating the URL to maintain the existing functionality
  const handleTrackPreview = async () => {
    if (!validateSpotifyUrl(spotifyUrl)) {
      return;
    }
    
    const preview = await handlePreviewTrack(spotifyUrl);
    if (preview && !trackTitle && preview.name) {
      setTrackTitle(preview.name);
    }
  };

  const processUpload = async (e: React.FormEvent) => {
    return handleUpload(
      e,
      spotifyUrl,
      selectedFile,
      trackPreview,
      validateSpotifyUrl,
      trackTitle,
      trackDescription
    );
  };

  return {
    spotifyUrl,
    isUrlValid,
    selectedFile,
    isUploading,
    isPreviewLoading,
    trackPreview,
    trackTitle,
    trackDescription,
    uploadError,
    handleSpotifyUrlChange,
    handleFileChange,
    handlePreviewTrack: handleTrackPreview,
    handleUpload: processUpload,
    setTrackPreview,
    setTrackTitle,
    setTrackDescription,
  };
}

export * from './validateSpotifyUrl';
export * from './useTrackPreview';
export * from './useFileUpload';
export * from './useUploadProcess';
export * from './useTrackDetails';
